import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Wallet, Lock, Unlock, ChevronRight, X, IndianRupee, Plus, Loader2, FileText, Share2, History, Sparkles, ShieldAlert, CheckCircle2, AlertTriangle } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BatchPicker } from "@/components/students/BatchPicker";
import { format } from "date-fns";
import api from "@/lib/api";
import { toast } from "sonner";
import { ResilientFetchBanner, runWithSettled } from "@/lib/resilientFetch";
import { fmt } from "@/lib/timezone";
import { useBrand } from "@/lib/brand";

const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: format(d, "MMM yyyy") });
  }
  return out;
};

const InstructorPayroll = ({ user, onLogout }) => {
  const { default_timezone } = useBrand();
  const [month, setMonth] = useState(monthOptions()[0].value);
  const [studioId, setStudioId] = useState("");
  const [studios, setStudios] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchFailures, setFetchFailures] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillSummary, setDrillSummary] = useState(null);
  const [drillSessions, setDrillSessions] = useState([]);
  const [drillPayments, setDrillPayments] = useState([]);
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordForm, setRecordForm] = useState({ amount: "", payment_method: "upi", reference_number: "", notes: "" });
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ amount: "", reason: "" });

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  // Makeup class dialog
  const [makeupOpen, setMakeupOpen] = useState(false);
  const [allBatches, setAllBatches] = useState([]);
  const [allStyles, setAllStyles] = useState([]);
  const [allUserInstructors, setAllUserInstructors] = useState([]);
  const [allMasterInstructors, setAllMasterInstructors] = useState([]);
  const [makeupForm, setMakeupForm] = useState({ batch_id: "", date: "", start_time: "18:00", end_time: "19:00", instructor_id: "", room_id: "" });
  const [makeupConflictCheck, setMakeupConflictCheck] = useState(null); // {ok, conflicts_count, conflicts: []}
  const [makeupCheckingConflicts, setMakeupCheckingConflicts] = useState(false);

  const isSuperAdmin = user?.role === "super_admin";

  const buildFetchers = () => {
    const map = {
      "payroll": () => api.get(`/payroll/summary?month=${month}${studioId ? `&studio_id=${studioId}` : ""}`),
    };
    if (studios.length === 0) map["studios"] = () => api.get("/studios");
    return map;
  };

  const fetchData = async () => {
    setLoading(true);
    const { results, failures } = await runWithSettled(buildFetchers());
    if (results["payroll"]) setSummaries(results["payroll"].data.summaries || []);
    if (results["studios"]) setStudios(results["studios"].data.studios || []);
    setFetchFailures(failures);
    setLoading(false);
  };

  const retryFailed = async () => {
    if (!fetchFailures.length) return;
    setRetrying(true);
    const all = buildFetchers();
    const onlyFailed = Object.fromEntries(fetchFailures.map((f) => [f.label, all[f.label]]).filter(([, fn]) => fn));
    const { results, failures } = await runWithSettled(onlyFailed);
    if (results["payroll"]) setSummaries(results["payroll"].data.summaries || []);
    if (results["studios"]) setStudios(results["studios"].data.studios || []);
    setFetchFailures(failures);
    setRetrying(false);
    if (!failures.length) toast.success("Refreshed");
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [month, studioId]);

  const openDrill = async (summary) => {
    setDrillSummary(summary);
    setDrillOpen(true);
    setDrillSessions([]);
    setDrillPayments([]);
    try {
      const [sRes, pRes] = await Promise.all([
        api.get(`/payroll/sessions?instructor_id=${summary.instructor_id}&month=${month}`),
        api.get(`/instructor-payments?instructor_id=${summary.instructor_id}&month=${month}`),
      ]);
      setDrillSessions(sRes.data.sessions || []);
      setDrillPayments(pRes.data.payments || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load details");
    }
  };

  const confirmPayroll = async () => {
    if (!drillSummary) return;
    try {
      await api.post(`/payroll/${drillSummary.instructor_id}/${month}/confirm`);
      toast.success("Payroll confirmed");
      const fresh = await api.get(`/payroll/summary?month=${month}&instructor_id=${drillSummary.instructor_id}`);
      const next = (fresh.data.summaries || [])[0];
      if (next) setDrillSummary(next);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to confirm");
    }
  };

  const unlockPayroll = async () => {
    if (!drillSummary) return;
    const reason = window.prompt("Unlock reason (required)?")?.trim();
    if (!reason) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/payroll/${drillSummary.instructor_id}/${month}/unlock`, { reason });
      toast.success("Payroll unlocked");
      const fresh = await api.get(`/payroll/summary?month=${month}&instructor_id=${drillSummary.instructor_id}`);
      const next = (fresh.data.summaries || [])[0];
      if (next) setDrillSummary(next);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unlock");
    }
  };

  const recordPayment = async () => {
    if (!drillSummary) return;
    const amt = parseFloat(recordForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/instructor-payments`, {
        instructor_id: drillSummary.instructor_id,
        month,
        amount: amt,
        payment_method: recordForm.payment_method,
        reference_number: recordForm.reference_number || null,
        notes: recordForm.notes || null,
      });
      toast.success("Payment recorded");
      setRecordOpen(false);
      setRecordForm({ amount: "", payment_method: "upi", reference_number: "", notes: "" });
      const [pRes, sRes] = await Promise.all([
        api.get(`/payroll/summary?month=${month}&instructor_id=${drillSummary.instructor_id}`),
        api.get(`/instructor-payments?instructor_id=${drillSummary.instructor_id}&month=${month}`),
      ]);
      const next = (pRes.data.summaries || [])[0];
      if (next) setDrillSummary(next);
      setDrillPayments(sRes.data.payments || []);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    }
  };

  const addAdjustment = async () => {
    if (!drillSummary) return;
    const amt = parseFloat(adjForm.amount);
    if (!amt || amt === 0) { toast.error("Enter a non-zero amount"); return; }
    if (!adjForm.reason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/payroll/${drillSummary.instructor_id}/${month}/adjustment`, { amount: amt, reason: adjForm.reason });
      toast.success("Adjustment added");
      setAdjOpen(false);
      setAdjForm({ amount: "", reason: "" });
      const fresh = await api.get(`/payroll/summary?month=${month}&instructor_id=${drillSummary.instructor_id}`);
      const next = (fresh.data.summaries || [])[0];
      if (next) setDrillSummary(next);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add adjustment");
    }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const res = await api.get(`/instructor-payments/${paymentId}/receipt/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll_receipt_${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download receipt");
    }
  };

  const shareReceipt = async (paymentId, instructorName) => {
    try {
      const res = await api.post(`/instructor-payments/${paymentId}/share-token`);
      const { public_pdf_url } = res.data;
      const fullUrl = `${window.location.origin}${public_pdf_url}`;
      const text = `Hi ${instructorName || ""}, here's your payroll receipt for ${month}: ${fullUrl}`;
      const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(wa, "_blank", "noopener");
      toast.success("Share link copied to WhatsApp");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to issue share link");
    }
  };

  const openAuditLog = async () => {
    if (!drillSummary) return;
    setAuditOpen(true);
    setAuditLogs([]);
    try {
      const res = await api.get(`/payroll/audit-logs?instructor_id=${drillSummary.instructor_id}&limit=50`);
      setAuditLogs(res.data.logs || []);
    } catch (e) {
      toast.error("Failed to load audit log");
    }
  };

  const openMakeupDialog = async () => {
    setMakeupOpen(true);
    if (allBatches.length === 0) {
      try {
        const [bRes, stRes, uRes, mRes] = await Promise.all([
          api.get("/batches"),
          api.get("/dance-styles"),
          api.get("/users?role=instructor"),
          api.get("/instructors"),
        ]);
        setAllBatches(bRes.data.batches || []);
        setAllStyles(stRes.data.styles || []);
        setAllUserInstructors(uRes.data.users || []);
        setAllMasterInstructors(mRes.data.instructors || []);
      } catch (err) {
        // Non-fatal: makeup dialog dropdowns will be empty if these fail
        console.warn("[InstructorPayroll] failed to preload makeup dropdowns", err);
      }
    }
  };

  const resetMakeupForm = () => {
    setMakeupForm({ batch_id: "", date: "", start_time: "18:00", end_time: "19:00", instructor_id: "", room_id: "" });
    setMakeupConflictCheck(null);
  };

  // The selected batch's studio for the room dropdown
  const selectedMakeupBatch = useMemo(
    () => allBatches.find((b) => b.batch_id === makeupForm.batch_id) || null,
    [allBatches, makeupForm.batch_id],
  );
  const selectedMakeupStudio = useMemo(
    () => (selectedMakeupBatch ? studios.find((s) => s.studio_id === selectedMakeupBatch.studio_id) : null),
    [studios, selectedMakeupBatch],
  );
  const makeupRoomOptions = useMemo(
    () => selectedMakeupStudio?.rooms || [],
    [selectedMakeupStudio],
  );

  const checkMakeupConflicts = async () => {
    if (!makeupForm.batch_id || !makeupForm.date) {
      toast.error("Batch and date are required");
      return;
    }
    if (!makeupForm.start_time || !makeupForm.end_time) {
      toast.error("Start and end times are required");
      return;
    }
    setMakeupCheckingConflicts(true);
    setMakeupConflictCheck(null);
    try {
      const res = await api.post("/sessions/makeup/check-conflicts", {
        batch_id: makeupForm.batch_id,
        date: new Date(makeupForm.date).toISOString(),
        start_time: makeupForm.start_time,
        end_time: makeupForm.end_time,
        room_id: makeupForm.room_id || null,
      });
      setMakeupConflictCheck(res.data);
      if (res.data.ok) toast.success("No conflicts in this room");
      else toast.warning(`${res.data.conflicts_count} conflict(s) found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to check conflicts");
    } finally {
      setMakeupCheckingConflicts(false);
    }
  };

  const submitMakeup = async () => {
    if (!makeupForm.batch_id || !makeupForm.date) { toast.error("Batch and date are required"); return; }
    try {
      await api.post("/sessions/makeup", {
        batch_id: makeupForm.batch_id,
        date: new Date(makeupForm.date).toISOString(),
        start_time: makeupForm.start_time,
        end_time: makeupForm.end_time,
        instructor_id: makeupForm.instructor_id || null,
        room_id: makeupForm.room_id || null,
      });
      toast.success("Makeup session created");
      setMakeupOpen(false);
      resetMakeupForm();
    } catch (e) {
      // Backend now returns 400 with {message, conflicts: []} when conflicts exist — surface them inline
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.conflicts)) {
        setMakeupConflictCheck({ ok: false, conflicts_count: detail.conflicts.length, conflicts: detail.conflicts });
        toast.error(detail.message || "Room has conflicts at this time");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to create makeup session");
      }
    }
  };

  // Safe number helpers — backend can occasionally omit fields (older locked rows, race conditions)
  const num = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : 0);
  const fmt = (v) => num(v).toLocaleString();

  const safeSummaries = useMemo(() => (summaries || []).filter(Boolean), [summaries]);
  const totalGross = useMemo(() => safeSummaries.reduce((s, x) => s + num(x?.gross_payable), 0), [safeSummaries]);
  const totalPaid = useMemo(() => safeSummaries.reduce((s, x) => s + num(x?.paid_amount), 0), [safeSummaries]);
  const totalPending = useMemo(() => safeSummaries.reduce((s, x) => s + num(x?.pending_amount), 0), [safeSummaries]);

  return (
    <div className="min-h-screen bg-slate-50 pb-20 lg:pb-0 lg:pl-72">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Wallet className="w-7 h-7 text-emerald-600" /> Instructor Payroll
            </h1>
            <p className="text-slate-500 text-sm mt-1">Monthly payable summary by instructor — auto-built from completed sessions.</p>
          </div>
          <Button onClick={openMakeupDialog} variant="outline" className="rounded-full" data-testid="open-makeup-btn">
            <Sparkles className="w-4 h-4 mr-1" /> + Makeup Class
          </Button>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-2xl p-4 border border-slate-100">
            <Label>Month</Label>
            <select value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-full h-10 rounded-xl border border-slate-200 px-3 bg-white text-sm" data-testid="payroll-month-select">
              {monthOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-100">
            <Label>Studio</Label>
            <select value={studioId} onChange={(e) => setStudioId(e.target.value)} className="mt-1 w-full h-10 rounded-xl border border-slate-200 px-3 bg-white text-sm" data-testid="payroll-studio-select">
              <option value="">All Studios</option>
              {studios.map((s) => <option key={s.studio_id} value={s.studio_id}>{s.name}</option>)}
            </select>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 col-span-1 sm:col-span-2 flex items-center justify-between">
            <div>
              <p className="text-xs text-emerald-700 font-medium uppercase tracking-wide">Total Gross — {format(new Date(`${month}-01`), "MMM yyyy")}</p>
              <p className="text-2xl font-bold text-emerald-900 mt-1">₹{fmt(totalGross)}</p>
              <p className="text-xs text-emerald-700 mt-1">Paid ₹{fmt(totalPaid)} · Pending ₹{fmt(totalPending)}</p>
            </div>
            <Wallet className="w-12 h-12 text-emerald-300" />
          </div>
        </div>

        {fetchFailures.length > 0 && (
          <ResilientFetchBanner
            failedLabels={fetchFailures.map((f) => f.label)}
            onRetry={retryFailed}
            retrying={retrying}
            className="mb-3"
            testid="payroll-fetch-error"
          />
        )}

        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
        ) : safeSummaries.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
            <Wallet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No payroll for this month yet.</p>
            <p className="text-xs text-slate-400 mt-1">Sessions become payable once marked complete.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
            {safeSummaries.map((s) => (
              <button
                key={s.instructor_id}
                onClick={() => openDrill(s)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left"
                data-testid={`payroll-row-${s.instructor_id}`}
              >
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-slate-900">{s.instructor_name || "—"}</p>
                    {s.status === "confirmed" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">
                        <Lock className="w-3 h-3" /> Confirmed
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-medium">Draft</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{num(s.total_sessions)} sessions · {num(s.total_hours).toFixed(1)} hrs · {(s.studios || []).length} studio(s)</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-base font-semibold text-emerald-700">₹{fmt(num(s.gross_payable) + num(s.adjustments_total))}</p>
                    <p className="text-[10px] text-slate-500">Pending ₹{fmt(s.pending_amount)}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <BottomNav user={user} onLogout={onLogout} />

      {/* Drill-down dialog */}
      <Dialog open={drillOpen} onOpenChange={(v) => { setDrillOpen(v); if (!v) { setDrillSummary(null); setDrillSessions([]); setDrillPayments([]); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drillSummary?.instructor_name || "Payroll detail"}
              {drillSummary?.status === "confirmed" && <Lock className="w-4 h-4 text-violet-600" />}
            </DialogTitle>
          </DialogHeader>

          {drillSummary && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Sessions" value={num(drillSummary.total_sessions)} />
                <Stat label="Total Hours" value={num(drillSummary.total_hours).toFixed(1)} />
                <Stat label="Gross" value={`₹${fmt(drillSummary.gross_payable)}`} accent="emerald" />
                <Stat label="Pending" value={`₹${fmt(drillSummary.pending_amount)}`} accent={num(drillSummary.pending_amount) > 0 ? "orange" : "slate"} />
              </div>
              {num(drillSummary.adjustments_total) !== 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  Adjustments applied: <strong>₹{fmt(drillSummary.adjustments_total)}</strong>
                </p>
              )}

              {/* Studio breakdown */}
              {(drillSummary.studios || []).length > 1 && (
                <div className="border rounded-xl p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Studio-wise Breakdown</p>
                  <div className="space-y-1">
                    {(drillSummary.studios || []).filter(Boolean).map((b) => (
                      <div key={b.studio_id} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{b.studio_name || "—"}</span>
                        <span className="text-slate-500">{num(b.sessions)} sessions · {num(b.hours).toFixed(1)} hr · ₹{fmt(b.gross)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Session list */}
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-2">Conducted Sessions</p>
                <div className="border rounded-xl divide-y max-h-80 overflow-y-auto">
                  {(drillSessions || []).length === 0 && <p className="p-3 text-sm text-slate-500">No sessions in this month</p>}
                  {(drillSessions || []).filter(Boolean).map((s) => (
                    <div key={s.session_id} className="flex items-center justify-between p-3 text-sm">
                      <div>
                        <p className="font-medium text-slate-900">{s.batch_name || "—"}</p>
                        <p className="text-xs text-slate-500">{s.studio_name || "—"} · {s.date ? fmt(s.date, "dd MMM yyyy", default_timezone) : "—"} · {s.start_time}–{s.end_time}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-emerald-700">₹{fmt(s.payable_amount_snapshot)}</p>
                        <p className="text-[10px] text-slate-400">{num(s.duration_hours_snapshot).toFixed(1)} hr × ₹{num(s.instructor_rate_snapshot)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payments */}
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-2">Payments Recorded</p>
                <div className="border rounded-xl divide-y">
                  {(drillPayments || []).length === 0 && <p className="p-3 text-sm text-slate-500">No payments recorded yet</p>}
                  {(drillPayments || []).filter(Boolean).map((p) => (
                    <div key={p.payment_id} className="flex items-center justify-between p-3 text-sm gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900">{p.payment_date ? fmt(p.payment_date, "dd MMM yyyy", default_timezone) : "—"}</p>
                        <p className="text-xs text-slate-500 capitalize">{p.payment_method} {p.reference_number ? `· ${p.reference_number}` : ""}</p>
                      </div>
                      <p className="text-sm font-semibold whitespace-nowrap">₹{fmt(p.amount)}</p>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => downloadReceipt(p.payment_id)} title="Download receipt" className="h-8 w-8 p-0" data-testid={`download-receipt-${p.payment_id}`}>
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => shareReceipt(p.payment_id, drillSummary?.instructor_name)} title="Share via WhatsApp" className="h-8 w-8 p-0 text-emerald-600" data-testid={`share-receipt-${p.payment_id}`}>
                          <Share2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
                <Button onClick={() => setRecordOpen(true)} className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="record-payment-btn">
                  <IndianRupee className="w-4 h-4 mr-1" /> Record Payment
                </Button>
                <Button variant="outline" onClick={() => setAdjOpen(true)} className="flex-1 rounded-full" data-testid="add-adjustment-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Adjustment
                </Button>
                <Button variant="outline" onClick={openAuditLog} className="flex-1 rounded-full" data-testid="open-audit-btn">
                  <History className="w-4 h-4 mr-1" /> Audit
                </Button>
                {drillSummary.status !== "confirmed" ? (
                  <Button onClick={confirmPayroll} className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="confirm-payroll-btn">
                    <Lock className="w-4 h-4 mr-1" /> Confirm Payroll
                  </Button>
                ) : isSuperAdmin ? (
                  <Button variant="outline" onClick={unlockPayroll} className="flex-1 rounded-full text-orange-600" data-testid="unlock-payroll-btn">
                    <Unlock className="w-4 h-4 mr-1" /> Unlock
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record payment dialog */}
      <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Amount (₹) *</Label>
              <Input type="number" min="0" value={recordForm.amount} onChange={(e) => setRecordForm({ ...recordForm, amount: e.target.value })} data-testid="record-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Method</Label>
              <select value={recordForm.payment_method} onChange={(e) => setRecordForm({ ...recordForm, payment_method: e.target.value })} className="w-full h-10 rounded-xl border border-slate-200 px-3 bg-white">
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Card</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Reference Number</Label>
              <Input value={recordForm.reference_number} onChange={(e) => setRecordForm({ ...recordForm, reference_number: e.target.value })} placeholder="Optional" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={recordForm.notes} onChange={(e) => setRecordForm({ ...recordForm, notes: e.target.value })} placeholder="Optional" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setRecordOpen(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button onClick={recordPayment} className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="record-payment-confirm">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjustment dialog */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Payroll Adjustment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Amount (₹) — positive for bonus, negative for deduction *</Label>
              <Input type="number" value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} data-testid="adj-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Reason *</Label>
              <Input value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} placeholder="e.g. Festival bonus" data-testid="adj-reason-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setAdjOpen(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button onClick={addAdjustment} className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="adj-confirm">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit log dialog */}
      <Dialog open={auditOpen} onOpenChange={(v) => { setAuditOpen(v); if (!v) setAuditLogs([]); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Payroll Audit Log — {drillSummary?.instructor_name}</DialogTitle></DialogHeader>
          {auditLogs.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No activity yet</p>
          ) : (
            <div className="border rounded-xl divide-y">
              {auditLogs.map((l) => (
                <div key={l.audit_id} className="p-3 text-sm">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{(l.action || "").replace(/_/g, " ")}</span>
                    <span className="text-xs text-slate-500">{l.created_at ? fmt(l.created_at, "dd MMM yyyy HH:mm", default_timezone) : ""}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {l.actor_name ? `By ${l.actor_name}` : ""}
                    {l.month ? ` · ${l.month}` : ""}
                    {l.scope ? ` · ${l.scope}` : ""}
                    {l.amount ? ` · ₹${l.amount}` : ""}
                    {l.reason ? ` · "${l.reason}"` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Makeup class dialog */}
      <Dialog open={makeupOpen} onOpenChange={(v) => { setMakeupOpen(v); if (!v) resetMakeupForm(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Create Makeup Class</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Batch *</Label>
              <BatchPicker
                value={makeupForm.batch_id}
                batches={allBatches}
                styles={allStyles}
                studios={studios}
                instructors={allUserInstructors}
                masterInstructors={allMasterInstructors}
                placeholder="Choose Batch"
                testIdPrefix="makeup-batch"
                onChange={(id) => { setMakeupForm({ ...makeupForm, batch_id: id, room_id: "" }); setMakeupConflictCheck(null); }}
              />
            </div>

            {/* Room — defaults to batch's room; admin can override */}
            <div className="space-y-1.5">
              <Label>Room {selectedMakeupStudio ? `(${selectedMakeupStudio.name})` : ""}</Label>
              <select
                value={makeupForm.room_id}
                onChange={(e) => { setMakeupForm({ ...makeupForm, room_id: e.target.value }); setMakeupConflictCheck(null); }}
                disabled={!selectedMakeupBatch}
                className="w-full h-10 rounded-xl border border-slate-200 px-3 bg-white text-sm disabled:bg-slate-50 disabled:text-slate-400"
                data-testid="makeup-room-select"
              >
                <option value="">{selectedMakeupBatch ? `Batch default${selectedMakeupBatch.room_id ? "" : " (no room set)"}` : "Pick a batch first"}</option>
                {makeupRoomOptions.map((r) => (
                  <option key={r.room_id} value={r.room_id}>{r.name}</option>
                ))}
              </select>
              {selectedMakeupBatch && !makeupForm.room_id && (
                <p className="text-[11px] text-slate-400">
                  Will use batch default: {makeupRoomOptions.find((r) => r.room_id === selectedMakeupBatch.room_id)?.name || "—"}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Date *</Label>
              <Input
                type="date"
                value={makeupForm.date}
                onChange={(e) => { setMakeupForm({ ...makeupForm, date: e.target.value }); setMakeupConflictCheck(null); }}
                data-testid="makeup-date-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>Start</Label>
                <Input
                  type="time"
                  value={makeupForm.start_time}
                  onChange={(e) => { setMakeupForm({ ...makeupForm, start_time: e.target.value }); setMakeupConflictCheck(null); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label>End</Label>
                <Input
                  type="time"
                  value={makeupForm.end_time}
                  onChange={(e) => { setMakeupForm({ ...makeupForm, end_time: e.target.value }); setMakeupConflictCheck(null); }}
                />
              </div>
            </div>

            {/* Conflict check */}
            <Button
              type="button"
              variant="outline"
              onClick={checkMakeupConflicts}
              disabled={makeupCheckingConflicts || !makeupForm.batch_id || !makeupForm.date}
              className="w-full rounded-full"
              data-testid="makeup-check-conflicts-btn"
            >
              {makeupCheckingConflicts ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</>
              ) : (
                <><ShieldAlert className="w-4 h-4 mr-2" /> Check Schedule Conflicts</>
              )}
            </Button>

            {makeupConflictCheck && makeupConflictCheck.ok && (
              <div data-testid="makeup-conflict-ok" className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600" />
                <div className="text-emerald-800">No conflicts found for this room at the selected time.</div>
              </div>
            )}

            {makeupConflictCheck && !makeupConflictCheck.ok && (
              <div data-testid="makeup-conflict-warn" className="rounded-xl bg-red-50 border border-red-200 p-3 space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-red-600" />
                  <div>
                    <p className="text-red-800 font-semibold">
                      {makeupConflictCheck.conflicts_count} conflict{makeupConflictCheck.conflicts_count > 1 ? "s" : ""} detected
                    </p>
                    <p className="text-red-700 text-xs">Change date, time or room before creating.</p>
                  </div>
                </div>
                <ul className="space-y-1 ml-6 list-disc">
                  {(makeupConflictCheck.conflicts || []).map((c, idx) => (
                    <li key={`${c.type}-${c.start_time}-${c.end_time}-${c.with || c.renter_name || c.batch_name || idx}`} data-testid={`makeup-conflict-item-${idx}`} className="text-red-800 text-xs">
                      {c.type === "rental" && (<><span className="font-medium">Rental</span> · {c.with || c.renter_name} · {c.start_time}–{c.end_time}</>)}
                      {c.type === "class" && (<><span className="font-medium">Class</span> · {c.with || c.batch_name} · {c.start_time}–{c.end_time}</>)}
                      {c.type === "session" && (<><span className="font-medium">{c.is_makeup ? "Makeup" : "Session"}</span> · {c.with} · {c.start_time}–{c.end_time}</>)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-slate-500">Makeup sessions go through the regular complete → snapshot flow. Mark complete via Attendance to add to payroll.</p>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setMakeupOpen(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button
                onClick={submitMakeup}
                disabled={!!(makeupConflictCheck && !makeupConflictCheck.ok)}
                className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white disabled:bg-slate-300"
                data-testid="makeup-confirm"
              >Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value, accent = "slate" }) => {
  const colors = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    orange: "bg-orange-50 text-orange-700",
  }[accent];
  return (
    <div className={`rounded-xl p-3 ${colors}`}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-lg font-bold mt-0.5">{value}</p>
    </div>
  );
};

export default InstructorPayroll;
